import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { TrendingUpIcon, MegaphoneIcon, MonitorIcon, DollarSignIcon, UsersIcon, HeadphonesIcon, CrownIcon, BuildingIcon, ShieldIcon, GlobeIcon } from 'lucide-react';
interface Niche {
  icon: React.ReactNode;
  title: string;
  positions: string[];
  color: string;
  bgColor: string;
  borderColor: string;
}
const niches: Niche[] = [{
  icon: <CrownIcon className="w-5 h-5" data-id="element-324" />,
  title: 'C‑level & Executive',
  positions: ['CEO / Генеральний директор', 'COO / Операційний директор', 'CFO / Фінансовий директор', 'CTO / Технічний директор', 'CMO / Директор з маркетингу', 'CHRO / Директор з персоналу', 'CPO / Директор з продукту', 'CSO / Директор зі стратегії'],
  color: 'text-navy-700',
  bgColor: 'bg-navy-100',
  borderColor: 'border-navy-300'
}, {
  icon: <MonitorIcon className="w-5 h-5" data-id="element-325" />,
  title: 'IT & Digital',
  positions: ['CTO / VP of Engineering', 'Tech Lead / Team Lead', 'Frontend‑розробник (React, Angular)', 'Backend‑розробник (Node, Go, Java)', 'Full‑stack розробник', 'DevOps / SRE інженер', 'QA Engineer / QA Lead', 'Product Manager / Product Owner', 'UI/UX Designer', 'Data Engineer / Data Scientist', 'Mobile Developer (iOS, Android)', 'Solutions Architect'],
  color: 'text-violet-600',
  bgColor: 'bg-violet-100',
  borderColor: 'border-violet-300'
}, {
  icon: <DollarSignIcon className="w-5 h-5" data-id="element-326" />,
  title: 'Фінанси та банкінг',
  positions: ['CFO / Фінансовий директор', 'Фінансовий аналітик', 'Головний бухгалтер', 'Аудитор / Внутрішній аудитор', 'Фінансовий контролер', 'Risk Manager', 'Compliance Officer', 'Treasury Manager'],
  color: 'text-emerald-600',
  bgColor: 'bg-emerald-100',
  borderColor: 'border-emerald-300'
}, {
  icon: <TrendingUpIcon className="w-5 h-5" data-id="element-327" />,
  title: 'Продажі та бізнес‑розвиток',
  positions: ['Комерційний директор', 'Керівник відділу продажів', 'Key Account Manager', 'Business Development Manager', 'Sales Director', 'Торговий представник', 'Head of Partnerships'],
  color: 'text-blue-600',
  bgColor: 'bg-blue-100',
  borderColor: 'border-blue-300'
}, {
  icon: <MegaphoneIcon className="w-5 h-5" data-id="element-328" />,
  title: 'Маркетинг та PR',
  positions: ['CMO / Директор з маркетингу', 'Digital‑маркетолог', 'Brand Manager', 'Performance Marketing Manager', 'PR‑директор', 'Content Marketing Manager', 'Growth Manager'],
  color: 'text-pink-600',
  bgColor: 'bg-pink-100',
  borderColor: 'border-pink-300'
}, {
  icon: <UsersIcon className="w-5 h-5" data-id="element-329" />,
  title: 'HR & People',
  positions: ['CHRO / HR Director', 'HR Business Partner', 'Head of Recruitment', 'C&B Manager', 'HR‑аналітик', 'L&D Manager', 'Employer Brand Manager'],
  color: 'text-primary-600',
  bgColor: 'bg-primary-100',
  borderColor: 'border-primary-300'
}, {
  icon: <BuildingIcon className="w-5 h-5" data-id="element-330" />,
  title: 'Логістика та операції',
  positions: ['COO / Операційний директор', 'Supply Chain Director', 'Логістичний менеджер', 'Директор з закупівель', 'Warehouse Manager', 'Operations Manager'],
  color: 'text-orange-600',
  bgColor: 'bg-orange-100',
  borderColor: 'border-orange-300'
}, {
  icon: <ShieldIcon className="w-5 h-5" data-id="element-331" />,
  title: 'Юриспруденція',
  positions: ['General Counsel / Юридичний директор', 'Corporate Lawyer', 'Compliance Manager', 'Юрист з трудового права', 'IP Lawyer'],
  color: 'text-amber-600',
  bgColor: 'bg-amber-100',
  borderColor: 'border-amber-300'
}, {
  icon: <GlobeIcon className="w-5 h-5" data-id="element-332" />,
  title: 'E‑commerce & Retail',
  positions: ['E‑commerce Director', 'Category Manager', 'Merchandiser', 'Retail Operations Manager', 'Marketplace Manager'],
  color: 'text-rose-600',
  bgColor: 'bg-rose-100',
  borderColor: 'border-rose-300'
}, {
  icon: <HeadphonesIcon className="w-5 h-5" data-id="element-333" />,
  title: 'Адміністрація та підтримка',
  positions: ['Персональний асистент CEO', 'Офіс‑менеджер', 'Executive Assistant', 'Координатор проєктів', 'Customer Success Manager'],
  color: 'text-teal-600',
  bgColor: 'bg-teal-100',
  borderColor: 'border-teal-300'
}];
export function NichesSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const active = niches[activeIndex];
  return <section className="py-20 lg:py-28 bg-navy-50/50" id="niches" data-id="element-334">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-335">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-336">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-337">
            Спеціалізація
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-338">
            Ніші та напрямки
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-339">
            Підбираємо спеціалістів у ключових галузях — від лінійних позицій до
            топ‑менеджменту
          </p>
        </motion.div>

        {/* Mobile: horizontal scroll tabs */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="lg:hidden mb-6 -mx-4 px-4" data-id="element-340">
          <div className="flex gap-2 overflow-x-auto pb-3 scrollbar-hide" data-id="element-341">
            {niches.map((niche, index) => <button key={niche.title} onClick={() => setActiveIndex(index)} className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${activeIndex === index ? `${niche.bgColor} ${niche.color} shadow-sm` : 'bg-white text-navy-500 border border-navy-200'}`} data-id="element-342">
                {niche.icon}
                {niche.title}
              </button>)}
          </div>
        </motion.div>

        {/* Mobile: positions panel */}
        <div className="lg:hidden" data-id="element-343">
          <AnimatePresence mode="wait" data-id="element-344">
            <motion.div key={activeIndex} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.2
          }} className="bg-white rounded-2xl border border-navy-100 p-6" data-id="element-345">
              <div className="flex items-center gap-3 mb-5" data-id="element-346">
                <div className={`w-10 h-10 rounded-xl ${active.bgColor} ${active.color} flex items-center justify-center`} data-id="element-347">
                  {active.icon}
                </div>
                <div data-id="element-348">
                  <h3 className="font-bold text-navy-900" data-id="element-349">{active.title}</h3>
                  <p className="text-xs text-navy-400" data-id="element-350">
                    {active.positions.length} позицій
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-351">
                {active.positions.map((pos, i) => <div key={pos} className="flex items-center gap-2.5 py-2 px-3 rounded-lg bg-navy-50/50 text-sm text-navy-600" data-id="element-352">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${active.bgColor}`} data-id="element-353" />
                    {pos}
                  </div>)}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Desktop: side-by-side tabs + panel */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.15
      }} className="hidden lg:grid lg:grid-cols-12 gap-6" data-id="element-354">
          {/* Tab list */}
          <div className="col-span-4 space-y-1.5" data-id="element-355">
            {niches.map((niche, index) => <button key={niche.title} onClick={() => setActiveIndex(index)} className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 group ${activeIndex === index ? `bg-white shadow-md border ${niche.borderColor}` : 'hover:bg-white/60 border border-transparent'}`} data-id="element-356">
                <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-200 ${activeIndex === index ? `${niche.bgColor} ${niche.color}` : 'bg-navy-100/60 text-navy-400 group-hover:bg-navy-100 group-hover:text-navy-500'}`} data-id="element-357">
                  {niche.icon}
                </div>
                <div className="flex-1 min-w-0" data-id="element-358">
                  <div className={`text-sm font-semibold truncate transition-colors ${activeIndex === index ? 'text-navy-900' : 'text-navy-600 group-hover:text-navy-800'}`} data-id="element-359">
                    {niche.title}
                  </div>
                  <div className="text-xs text-navy-400" data-id="element-360">
                    {niche.positions.length} позицій
                  </div>
                </div>
                {activeIndex === index && <motion.div layoutId="niche-indicator" className={`w-1.5 h-8 rounded-full ${niche.bgColor}`} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 30
            }} data-id="element-361" />}
              </button>)}
          </div>

          {/* Content panel */}
          <div className="col-span-8" data-id="element-362">
            <AnimatePresence mode="wait" data-id="element-363">
              <motion.div key={activeIndex} initial={{
              opacity: 0,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -12
            }} transition={{
              duration: 0.25
            }} className="bg-white rounded-2xl border border-navy-100 shadow-sm p-8 min-h-[420px]" data-id="element-364">
                {/* Panel header */}
                <div className="flex items-center gap-4 mb-8 pb-6 border-b border-navy-100" data-id="element-365">
                  <div className={`w-14 h-14 rounded-2xl ${active.bgColor} ${active.color} flex items-center justify-center`} data-id="element-366">
                    <div className="scale-125" data-id="element-367">{active.icon}</div>
                  </div>
                  <div data-id="element-368">
                    <h3 className="text-2xl font-extrabold text-navy-900" data-id="element-369">
                      {active.title}
                    </h3>
                    <p className="text-sm text-navy-400 mt-0.5" data-id="element-370">
                      {active.positions.length} напрямків підбору
                    </p>
                  </div>
                </div>

                {/* Positions grid */}
                <div className="grid grid-cols-2 gap-3" data-id="element-371">
                  {active.positions.map((pos, i) => <motion.div key={pos} initial={{
                  opacity: 0,
                  y: 6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.2,
                  delay: i * 0.03
                }} className="flex items-center gap-3 py-3 px-4 rounded-xl bg-navy-50/40 border border-navy-100/50" data-id="element-372">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${active.bgColor}`} data-id="element-373" />
                      <span className="text-sm font-medium text-navy-700" data-id="element-374">
                        {pos}
                      </span>
                    </motion.div>)}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>;
}